import {
  createFolder as createFolderBackend,
  deleteFolder as deleteFolderBackend,
  getFolderTree,
  updateFolder,
} from '@/backend/folder'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { serializeFolder } from './serializers'
import type { FolderInTree, FolderTrees } from './types'

/**
 * The folder store is responsible for loading and storing the folder trees for
 * the current workspace. Any Go domain object (e.g. Agent, Case, etc.) can
 * be placed into a folder via a `parentFolderId` field. Those links aren't
 * stored here, it is expected that those links will be computed in components
 * where the folder is displayed.
 */
export const useFolderStore = defineStore('folders', () => {
  const folderTrees = ref<FolderTrees>([])
  const folderMap = ref<Record<string, Pick<FolderInTree, 'id' | 'name'>>>({})

  const loadFolderTrees = async (workspaceId: string) => {
    const result = await getFolderTree(workspaceId)
    if (!result.ok) {
      return
    }

    folderTrees.value = result.data.root_nodes.map(serializeFolder)

    const populateFolderMap = (folder: FolderInTree) => {
      folderMap.value[folder.id] = {
        id: folder.id,
        name: folder.name,
      }
      folder.children.forEach(populateFolderMap)
    }
    folderTrees.value.forEach(populateFolderMap)
  }

  const createFolder = async ({
    name,
    parentFolderId,
    workspaceId,
  }: {
    workspaceId: string
    name?: string | null
    parentFolderId?: string
  }) => {
    const optimisticFolder: FolderInTree = {
      id: '00000000-0000-0000-0000-000000000000',
      name: name || null,
      children: [],
      nodeType: 'folder',
    }
    const oldFolderTrees = folderTrees.value
    if (parentFolderId) {
      folderTrees.value = folderTrees.value.map((tree) =>
        _addFolder({ folderToAdd: optimisticFolder, parentId: parentFolderId, tree }),
      )
    } else {
      folderTrees.value.push(optimisticFolder)
    }

    const res = await createFolderBackend({
      workspaceId,
      name,
      parentFolderId,
    })

    if (res.ok) {
      folderTrees.value = folderTrees.value
        .map((tree) => _deleteFolder({ folderId: optimisticFolder.id, tree }))
        .filter((t) => !!t)
      if (parentFolderId) {
        folderTrees.value = folderTrees.value.map((tree) =>
          _addFolder({
            folderToAdd: {
              id: res.data.id,
              name: res.data.name,
              children: [],
              nodeType: 'folder',
            },
            parentId: parentFolderId,
            tree,
          }),
        )
      } else {
        folderTrees.value.push({
          id: res.data.id,
          name: res.data.name,
          children: [],
          nodeType: 'folder',
        })
      }
    } else {
      folderTrees.value = oldFolderTrees
    }

    return res
  }

  const renameFolder = async ({
    folderId,
    workspaceId,
    name,
  }: {
    folderId: string
    workspaceId: string
    name: string
  }) => {
    const oldFolderTrees = folderTrees.value
    folderTrees.value = folderTrees.value.map((tree) => _updateFolderName({ folderId, name, tree }))

    const res = await updateFolder({ workspaceId, folderId, name })

    if (!res.ok) {
      folderTrees.value = oldFolderTrees
    }

    return res
  }

  const deleteFolder = async ({
    folderId,
    workspaceId,
  }: {
    folderId: string
    workspaceId: string
  }) => {
    const oldFolderTrees = folderTrees.value
    folderTrees.value = folderTrees.value
      .map((tree) => _deleteFolder({ folderId, tree }))
      .filter((t) => !!t)
    const res = await deleteFolderBackend({ folderId, workspaceId })
    if (!res.ok && res.error.status !== 404) {
      folderTrees.value = oldFolderTrees
    }

    return res
  }

  const $reset = () => {
    folderTrees.value = []
  }

  return {
    folderTrees,
    loadFolderTrees,

    folderMap,

    createFolder,
    renameFolder,
    deleteFolder,

    $reset,
  }
})

/** Recursive function that deletes a folder from the tree, returning the updated tree */
function _deleteFolder({
  folderId,
  tree,
}: {
  folderId: string
  tree: FolderInTree
}): FolderInTree | null {
  if (tree.id === folderId) {
    return null
  }

  return {
    ...tree,
    children: tree.children.reduce<FolderInTree[]>((acc, child) => {
      if (child.id === folderId) {
        return acc
      }

      const newSubtree = _deleteFolder({ folderId, tree: child })
      if (newSubtree) {
        acc.push(newSubtree)
      }

      return acc
    }, []),
  }
}

/**
 * Recursive function that adds a folder to the tree under the given parent, returning the
 * updated tree
 */
function _addFolder({
  folderToAdd,
  parentId,
  tree,
}: {
  parentId: string
  tree: FolderInTree
  folderToAdd: FolderInTree
}): FolderInTree {
  if (tree.id === parentId) {
    tree.children.push(folderToAdd)
  }

  return {
    ...tree,
    children: tree.children.map((child) => _addFolder({ folderToAdd, parentId, tree: child })),
  }
}

/**
 * Recursive function that updates a folder's name in the tree, returning the updated tree
 */
function _updateFolderName({
  folderId,
  name,
  tree,
}: {
  folderId: string
  name: string
  tree: FolderInTree
}): FolderInTree {
  if (tree.id === folderId) {
    return {
      ...tree,
      name,
    }
  }

  return {
    ...tree,
    children: tree.children.map((child) => _updateFolderName({ folderId, name, tree: child })),
  }
}
